import React, { createContext, useContext, useState, useCallback } from "react";
import groupService, { Group } from "../services/api/groupService";
import postService, { Post, Comment } from "../services/api/postService";
import discussionService, {
  Discussion,
} from "../services/api/discussionService";

const communityService = {
  ...groupService,
  ...postService,
  ...discussionService,
};
import { useUser } from "./UserContext";

interface CommunityContextState {
  // Group data
  userGroups: Group[];
  popularGroups: Group[];
  suggestedGroups: Group[];

  // Post data
  latestPosts: Post[];
  trendingPosts: Post[];
  latestDiscussions: Discussion[];

  // Loading states
  isUserGroupsLoading: boolean;
  isPopularGroupsLoading: boolean;
  isSuggestedGroupsLoading: boolean;
  isLatestPostsLoading: boolean;
  isTrendingPostsLoading: boolean;
  isLatestDiscussionsLoading: boolean;

  // Error states
  userGroupsError: string | null;
  popularGroupsError: string | null;
  suggestedGroupsError: string | null;
  latestPostsError: string | null;
  trendingPostsError: string | null;
  latestDiscussionsError: string | null;

  // Group actions
  getGroup: (idOrSlug: string) => Promise<Group>;
  createGroup: (data: any) => Promise<Group>;
  updateGroup: (groupId: string, data: any) => Promise<Group>;
  deleteGroup: (groupId: string) => Promise<void>;
  joinGroup: (groupId: string) => Promise<void>;
  leaveGroup: (groupId: string) => Promise<void>;

  // Post actions
  getGroupPosts: (groupId: string) => Promise<Post[]>;
  createPost: (groupId: string, data: any) => Promise<Post>;
  updatePost: (postId: string, content: string) => Promise<Post>;
  deletePost: (postId: string) => Promise<void>;
  getPost: (postId: string) => Promise<Post>;
  likePost: (postId: string) => Promise<void>;
  unlikePost: (postId: string) => Promise<void>;

  // Comment actions
  getPostComments: (postId: string) => Promise<Comment[]>;
  createComment: (
    postId: string,
    content: string,
    parentId?: string,
  ) => Promise<Comment>;
  updateComment: (commentId: string, content: string) => Promise<Comment>;
  deleteComment: (commentId: string) => Promise<void>;
  likeComment: (commentId: string) => Promise<void>;
  unlikeComment: (commentId: string) => Promise<void>;

  // Refresh actions
  refreshUserGroups: () => Promise<void>;
  refreshPopularGroups: () => Promise<void>;
  refreshSuggestedGroups: () => Promise<void>;
  refreshLatestPosts: () => Promise<void>;
  refreshTrendingPosts: () => Promise<void>;
  refreshLatestDiscussions: () => Promise<void>;

  // Search states
  searchLoading: boolean;
  searchError: string | null;
  searchResults: {
    discussions: Discussion[];
    groups: Group[];
  };

  // Search methods
  searchDiscussions: (query: string) => Promise<void>;
  searchGroups: (query: string) => Promise<void>;
  clearSearch: () => void;
}

const CommunityContext = createContext<CommunityContextState | undefined>(
  undefined,
);

export const CommunityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();

  // Group data states
  const [userGroups, setUserGroups] = useState<Group[]>([]);
  const [popularGroups, setPopularGroups] = useState<Group[]>([]);
  const [suggestedGroups, setSuggestedGroups] = useState<Group[]>([]);

  // Post data states
  const [latestPosts, setLatestPosts] = useState<Post[]>([]);
  const [trendingPosts, setTrendingPosts] = useState<Post[]>([]);
  const [latestDiscussions, setLatestDiscussions] = useState<Discussion[]>([]);

  // Loading states
  const [isUserGroupsLoading, setIsUserGroupsLoading] = useState(true);
  const [isPopularGroupsLoading, setIsPopularGroupsLoading] = useState(true);
  const [isSuggestedGroupsLoading, setIsSuggestedGroupsLoading] =
    useState(true);
  const [isLatestPostsLoading, setIsLatestPostsLoading] = useState(true);
  const [isTrendingPostsLoading, setIsTrendingPostsLoading] = useState(true);
  const [isLatestDiscussionsLoading, setIsLatestDiscussionsLoading] =
    useState(true);

  // Error states
  const [userGroupsError, setUserGroupsError] = useState<string | null>(null);
  const [popularGroupsError, setPopularGroupsError] = useState<string | null>(
    null,
  );
  const [suggestedGroupsError, setSuggestedGroupsError] = useState<
    string | null
  >(null);
  const [latestPostsError, setLatestPostsError] = useState<string | null>(null);
  const [trendingPostsError, setTrendingPostsError] = useState<string | null>(
    null,
  );
  const [latestDiscussionsError, setLatestDiscussionsError] = useState<
    string | null
  >(null);

  // Search states
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<{
    discussions: Discussion[];
    groups: Group[];
  }>({
    discussions: [],
    groups: [],
  });

  // Load user groups
  const loadUserGroups = useCallback(async () => {
    if (!user) {
      setUserGroups([]);
      setIsUserGroupsLoading(false);
      return;
    }

    setIsUserGroupsLoading(true);
    setUserGroupsError(null);

    try {
      const { groups } = await communityService.getGroups({
        limit: 10,
        type: "public",
      });
      setUserGroups(groups);
    } catch (err) {
      setUserGroupsError(
        err instanceof Error ? err.message : "Failed to load user groups",
      );
    } finally {
      setIsUserGroupsLoading(false);
    }
  }, [user]);

  // Load popular groups
  const loadPopularGroups = useCallback(async () => {
    setIsPopularGroupsLoading(true);
    setPopularGroupsError(null);

    try {
      const { groups } = await communityService.getGroups({
        limit: 5,
        type: "public",
      });
      setPopularGroups(groups);
    } catch (err) {
      setPopularGroupsError(
        err instanceof Error ? err.message : "Failed to load popular groups",
      );
    } finally {
      setIsPopularGroupsLoading(false);
    }
  }, []);

  // Load suggested groups
  const loadSuggestedGroups = useCallback(async () => {
    if (!user) {
      setSuggestedGroups([]);
      setIsSuggestedGroupsLoading(false);
      return;
    }

    setIsSuggestedGroupsLoading(true);
    setSuggestedGroupsError(null);

    try {
      const { groups } = await communityService.getGroups({
        limit: 5,
        type: "public",
      });
      setSuggestedGroups(groups);
    } catch (err) {
      setSuggestedGroupsError(
        err instanceof Error ? err.message : "Failed to load suggested groups",
      );
    } finally {
      setIsSuggestedGroupsLoading(false);
    }
  }, [user]);

  // Load latest posts
  const loadLatestPosts = useCallback(async () => {
    setIsLatestPostsLoading(true);
    setLatestPostsError(null);

    try {
      const { posts } = await communityService.getGroupPosts("all", {
        limit: 10,
        sortBy: "recent",
      });
      setLatestPosts(posts);
    } catch (err) {
      setLatestPostsError(
        err instanceof Error ? err.message : "Failed to load latest posts",
      );
    } finally {
      setIsLatestPostsLoading(false);
    }
  }, []);

  // Load trending posts
  const loadTrendingPosts = useCallback(async () => {
    setIsTrendingPostsLoading(true);
    setTrendingPostsError(null);

    try {
      const { posts } = await communityService.getGroupPosts("all", {
        limit: 10,
        sortBy: "popular",
      });
      setTrendingPosts(posts);
    } catch (err) {
      setTrendingPostsError(
        err instanceof Error ? err.message : "Failed to load trending posts",
      );
    } finally {
      setIsTrendingPostsLoading(false);
    }
  }, []);

  // Load latest discussions
  const loadLatestDiscussions = useCallback(async () => {
    setIsLatestDiscussionsLoading(true);
    setLatestDiscussionsError(null);

    try {
      const { discussions } = await communityService.getDiscussions({
        limit: 10,
        sortBy: "recent",
      });
      setLatestDiscussions(discussions);
    } catch (err) {
      setLatestDiscussionsError(
        err instanceof Error
          ? err.message
          : "Failed to load latest discussions",
      );
    } finally {
      setIsLatestDiscussionsLoading(false);
    }
  }, []);

  // Initial data load - Removed to prevent redundant fetching on every page navigation
  // useEffect(() => {
  //   loadUserGroups();
  //   loadPopularGroups();
  //   loadSuggestedGroups();
  //   loadLatestPosts();
  //   loadTrendingPosts();
  // }, [
  //   loadUserGroups,
  //   loadPopularGroups,
  //   loadSuggestedGroups,
  //   loadLatestPosts,
  //   loadTrendingPosts,
  // ]);

  // Group actions
  const getGroup = async (idOrSlug: string): Promise<Group> => {
    try {
      return await communityService.getGroup(idOrSlug);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to load group",
      );
    }
  };

  const createGroup = async (data: any): Promise<Group> => {
    try {
      const newGroup = await communityService.createGroup(data);
      setUserGroups((prev) => [newGroup, ...prev]);
      return newGroup;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to create group",
      );
    }
  };

  const updateGroup = async (groupId: string, data: any): Promise<Group> => {
    try {
      const updatedGroup = await communityService.updateGroup(groupId, data);

      // Update group in all lists
      const updateGroupInList = (groups: Group[]) =>
        groups.map((group) => (group.id === groupId ? updatedGroup : group));

      setUserGroups((prev) => updateGroupInList(prev));
      setPopularGroups((prev) => updateGroupInList(prev));
      setSuggestedGroups((prev) => updateGroupInList(prev));

      return updatedGroup;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to update group",
      );
    }
  };

  const deleteGroup = async (groupId: string): Promise<void> => {
    try {
      await communityService.deleteGroup(groupId);

      // Remove group from all lists
      const removeGroupFromList = (groups: Group[]) =>
        groups.filter((group) => group.id !== groupId);

      setUserGroups(removeGroupFromList);
      setPopularGroups(removeGroupFromList);
      setSuggestedGroups(removeGroupFromList);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to delete group",
      );
    }
  };

  const joinGroup = async (groupId: string): Promise<void> => {
    try {
      await communityService.joinGroup(groupId);

      // Update member count in all lists
      const updateMemberCount = (groups: Group[]) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              memberCount: group.memberCount + 1,
            };
          }
          return group;
        });

      setUserGroups(updateMemberCount);
      setPopularGroups(updateMemberCount);
      setSuggestedGroups(updateMemberCount);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to join group",
      );
    }
  };

  const leaveGroup = async (groupId: string): Promise<void> => {
    try {
      await communityService.leaveGroup(groupId);

      // Update member count in all lists
      const updateMemberCount = (groups: Group[]) =>
        groups.map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              memberCount: group.memberCount - 1,
            };
          }
          return group;
        });

      setUserGroups((prev) => prev.filter((group) => group.id !== groupId));
      setPopularGroups(updateMemberCount);
      setSuggestedGroups(updateMemberCount);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to leave group",
      );
    }
  };

  // Post actions
  const getGroupPosts = async (groupId: string): Promise<Post[]> => {
    try {
      const { posts } = await communityService.getGroupPosts(groupId);
      return posts;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to load posts",
      );
    }
  };

  const createPost = async (groupId: string, data: any): Promise<Post> => {
    try {
      const newPost = await communityService.createPost(groupId, data);
      setLatestPosts((prev) => [newPost, ...prev]);
      return newPost;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to create post",
      );
    }
  };

  const updatePost = async (postId: string, content: string): Promise<Post> => {
    try {
      const updatedPost = await communityService.updatePost(postId, content);

      // Update post in all lists
      const updatePostInList = (posts: Post[]) =>
        posts.map((post) => (post.id === postId ? updatedPost : post));

      setLatestPosts(updatePostInList);
      setTrendingPosts(updatePostInList);

      return updatedPost;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to update post",
      );
    }
  };

  const deletePost = async (postId: string): Promise<void> => {
    try {
      await communityService.deletePost(postId);

      // Remove post from all lists
      const removePostFromList = (posts: Post[]) =>
        posts.filter((post) => post.id !== postId);

      setLatestPosts(removePostFromList);
      setTrendingPosts(removePostFromList);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to delete post",
      );
    }
  };

  const getPost = async (postId: string): Promise<Post> => {
    try {
      return await communityService.getPost(postId);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to load post",
      );
    }
  };

  const likePost = async (postId: string): Promise<void> => {
    try {
      await communityService.likePost(postId);

      // Update like count in all lists
      const updateLikes = (posts: Post[]) =>
        posts.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              likes: post.likes + 1,
            };
          }
          return post;
        });

      setLatestPosts(updateLikes);
      setTrendingPosts(updateLikes);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to like post",
      );
    }
  };

  const unlikePost = async (postId: string): Promise<void> => {
    try {
      await communityService.unlikePost(postId);

      // Update like count in all lists
      const updateLikes = (posts: Post[]) =>
        posts.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              likes: post.likes - 1,
            };
          }
          return post;
        });

      setLatestPosts(updateLikes);
      setTrendingPosts(updateLikes);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to unlike post",
      );
    }
  };

  // Comment actions
  const getPostComments = async (postId: string): Promise<Comment[]> => {
    try {
      const { comments } = await communityService.getPostComments(postId);
      return comments;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to load comments",
      );
    }
  };

  const createComment = async (
    postId: string,
    content: string,
    parentId?: string,
  ): Promise<Comment> => {
    try {
      return await communityService.createComment(postId, content, parentId);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to create comment",
      );
    }
  };

  const updateComment = async (
    commentId: string,
    content: string,
  ): Promise<Comment> => {
    try {
      return await communityService.updateComment(commentId, content);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to update comment",
      );
    }
  };

  const deleteComment = async (commentId: string): Promise<void> => {
    try {
      await communityService.deleteComment(commentId);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to delete comment",
      );
    }
  };

  const likeComment = async (commentId: string): Promise<void> => {
    try {
      await communityService.likeComment(commentId);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to like comment",
      );
    }
  };

  const unlikeComment = async (commentId: string): Promise<void> => {
    try {
      await communityService.unlikeComment(commentId);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to unlike comment",
      );
    }
  };

  // Search methods
  const searchDiscussions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults((prev) => ({ ...prev, discussions: [] }));
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await communityService.searchDiscussions({ query });
      if (results) {
        setSearchResults((prev) => ({
          ...prev,
          discussions: results.discussions,
        }));
      }
    } catch (error) {
      setSearchError("Failed to search discussions");
      console.error("Search discussions error:", error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const searchGroups = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults((prev) => ({ ...prev, groups: [] }));
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = (await communityService.searchGroups({
        query,
      })) as any;
      if (results && results.groups) {
        setSearchResults((prev) => ({ ...prev, groups: results.groups }));
      } else if (Array.isArray(results)) {
        setSearchResults((prev) => ({ ...prev, groups: results }));
      }
    } catch (error) {
      setSearchError("Failed to search groups");
      console.error("Search groups error:", error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults({
      discussions: [],
      groups: [],
    });
    setSearchError(null);
  }, []);

  const value: CommunityContextState = {
    // Group data
    userGroups,
    popularGroups,
    suggestedGroups,

    // Post data
    latestPosts,
    trendingPosts,
    latestDiscussions,

    // Loading states
    isUserGroupsLoading,
    isPopularGroupsLoading,
    isSuggestedGroupsLoading,
    isLatestPostsLoading,
    isTrendingPostsLoading,
    isLatestDiscussionsLoading,

    // Error states
    userGroupsError,
    popularGroupsError,
    suggestedGroupsError,
    latestPostsError,
    trendingPostsError,
    latestDiscussionsError,

    // Group actions
    getGroup,
    createGroup,
    updateGroup,
    deleteGroup,
    joinGroup,
    leaveGroup,

    // Post actions
    getGroupPosts,
    createPost,
    updatePost,
    deletePost,
    getPost,
    likePost,
    unlikePost,

    // Comment actions
    getPostComments,
    createComment,
    updateComment,
    deleteComment,
    likeComment,
    unlikeComment,

    // Refresh actions
    refreshUserGroups: loadUserGroups,
    refreshPopularGroups: loadPopularGroups,
    refreshSuggestedGroups: loadSuggestedGroups,
    refreshLatestPosts: loadLatestPosts,
    refreshTrendingPosts: loadTrendingPosts,
    refreshLatestDiscussions: loadLatestDiscussions,

    // Search states
    searchLoading,
    searchError,
    searchResults,
    searchDiscussions,
    searchGroups,
    clearSearch,
  };

  return (
    <CommunityContext.Provider value={value}>
      {children}
    </CommunityContext.Provider>
  );
};

export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (context === undefined) {
    throw new Error("useCommunity must be used within a CommunityProvider");
  }
  return context;
};

export default CommunityContext;
